import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

/**
 * Map Stripe SetupIntent error codes to user-friendly messages
 * Per Stripe docs: https://docs.stripe.com/error-codes
 */
function getSetupIntentErrorMessage(error: {
  code?: string;
  decline_code?: string;
  message?: string;
}): string {
  if (error.decline_code) {
    switch (error.decline_code) {
      case "insufficient_funds":
        return "Your card has insufficient funds. Please try a different card.";
      case "lost_card":
      case "stolen_card":
        return "This card cannot be used. Please try a different card.";
      case "generic_decline":
        return "Your card was declined. Please try a different card or contact your bank.";
      default:
        break;
    }
  }

  switch (error.code) {
    case "card_declined":
      return "Your card was declined. Please try a different card.";
    case "expired_card":
      return "Your card has expired. Please use a different card.";
    case "incorrect_cvc":
      return "The security code (CVC) is incorrect. Please check and try again.";
    case "incorrect_number":
      return "The card number is incorrect. Please check and try again.";
    case "invalid_expiry_month":
    case "invalid_expiry_year":
      return "The expiration date is invalid. Please check and try again.";
    case "processing_error":
      return "An error occurred while processing your card. Please try again.";
    case "authentication_required":
      return "Your bank requires additional verification. Please complete the authentication.";
    case "payment_method_provider_decline":
      return "Your bank declined this card. Please try a different card or contact your bank.";
    case "setup_intent_authentication_failure":
      return "Card authentication failed. Please try again or use a different card.";
    case "invalid_cvc":
      return "The security code (CVC) is invalid. Please check and try again.";
    default:
      return (
        error.message ?? "Failed to save payment method. Please try again."
      );
  }
}

export function PaymentMethodForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!(stripe && elements)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        const userMessage = getSetupIntentErrorMessage(error);
        toast.error(userMessage);
      } else {
        ProductEvents.paymentMethodUpdated();
        toast.success("Payment method saved successfully");
        await onSuccess();
      }
    } catch (err) {
      logger.exception("stripe_setup_confirm_failed", err);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <p className="text-sm font-medium">Card Details</p>
        <PaymentElement />
      </div>

      <div className="flex gap-3">
        <Button disabled={!stripe || isSubmitting} type="submit">
          {isSubmitting ? (
            <>
              <Spinner className="mr-2 size-4" />
              Saving...
            </>
          ) : (
            "Save Payment Method"
          )}
        </Button>
        <Button
          disabled={isSubmitting}
          onClick={onCancel}
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
